import axiosInstance from "../../api/axiosConfig";
import { VAPID_PUBLIC_KEY, urlBase64ToUint8Array } from "../../constants/push";
import { superadminAuthHeaders } from "./superadminSession";

/**
 * Web Push para la PWA de /admin. Canal paralelo al WhatsApp de plataforma: si esa
 * sesión se cae, las alertas siguen llegando por aquí.
 *
 * iOS solo permite push cuando la app está instalada en la pantalla de inicio; en
 * Safari "de a pie" reportamos 'needs-install' para explicárselo al usuario.
 */
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function getPushCapability() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return isIos() && !isStandalone() ? "needs-install" : "unsupported";
  }
  if (isIos() && !isStandalone()) return "needs-install";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/** ¿Este navegador ya tiene una suscripción activa? (para pintar el estado sin pedir permiso) */
export async function hasActiveSubscription() {
  try {
    if (getPushCapability() !== "granted") return false;
    const reg = await navigator.serviceWorker.getRegistration("/push.js");
    const sub = await reg?.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/** Debe llamarse desde un gesto del usuario (iOS lo exige). */
export async function enableSuperadminPush() {
  const reg = await navigator.serviceWorker.register("/push.js");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm;
  const subscription =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) }));
  await axiosInstance.post("/system-config/push-subscription", { subscription }, { headers: superadminAuthHeaders() });
  return "granted";
}

export async function disableSuperadminPush() {
  const reg = await navigator.serviceWorker.getRegistration("/push.js");
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await axiosInstance.delete("/system-config/push-subscription", { data: { endpoint: sub.endpoint }, headers: superadminAuthHeaders() }).catch(() => undefined);
  await sub.unsubscribe();
}

export async function sendTestPush() {
  const { data } = await axiosInstance.post("/system-config/push-test", {}, { headers: superadminAuthHeaders() });
  return data?.sent ?? 0;
}
